import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Paperclip, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";
import {
  inventoryManagementService,
  usesCustomerWarrantyReplacementType,
  usesPurchaseFields,
  usesWarDefRefJournal,
  warDefRefMovementType,
} from "../../../services/inventoryManagementService";
import {
  fetchVendorById,
  searchVendorsByTradeName,
  searchVendorsByLegalName,
} from "../../../services/productVendorService";
import {
  fetchWarehouseById,
  searchWarehousesByName,
} from "../../../services/warehouseService";

const LOOKUP_PAGE_SIZE = 10;
const JOURNAL_LOOKUP_PAGE_SIZE = 50;
const MAX_IMAGES_PER_FIELD = 10;
const MAX_IMAGE_BYTES = 500 * 1024; // 500 KB

const fieldLabelClass = "text-sm font-semibold text-foreground";
const fieldInputClass =
  "w-full h-10 px-3 rounded-xl border bg-white text-sm font-medium focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10";
const selectTriggerClass =
  "h-10 rounded-xl border bg-white text-sm font-medium focus:ring-accent w-full";

const WARRANTY_TYPE_OPTIONS = [
  { value: "Under_Warranty", labelKey: "inventory.warrantyUnderWarranty" },
  { value: "No_Warranty", labelKey: "inventory.warrantyNoWarranty" },
  { value: "Chargeable", labelKey: "inventory.warrantyChargeable" },
  { value: "Non_Chargeable", labelKey: "inventory.warrantyNonChargeable" },
];

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function createEmptyForm() {
  return {
    productId: "",
    batchNumber: "",
    receiptDate: todayIsoDate(),
    returnDate: todayIsoDate(),
    receivedQty: 0,
    defectiveQty: 0,
    remarks: "",
    inventoryImages: [],
    defectiveImages: [],
    purchasePricePerUnit: "",
    warrantyStartDate: "",
    warrantyEndDate: "",
    vendorId: "",
    customerId: "",
    poNumber: "",
    vendorInvoiceNumber: "",
    customerInvoiceId: "",
    invoiceDate: todayIsoDate(),
    projectName: "",
    iofNumber: "",
    warehouseId: "",
    warDefRefJournalId: "",
    customerWarrantyReplacementType: "",
  };
}

function ensureLookupOption(list, id, name, extra = {}) {
  if (id == null || String(id).trim() === "") return list;
  const sid = String(id);
  if ((list || []).some((item) => String(item.id) === sid)) return list;
  return [...(list || []), { id: sid, name: name || sid, ...extra }];
}

function mergeUniqueById(existing, incoming) {
  const map = new Map();
  [...(existing || []), ...(incoming || [])].forEach((item) => {
    if (item?.id == null || item.id === "") return;
    map.set(String(item.id), item);
  });
  return Array.from(map.values());
}

function formatJournalOptionDate(isoStr) {
  if (!isoStr) return "—";
  const dateObj = new Date(isoStr);
  if (Number.isNaN(dateObj.getTime())) return String(isoStr);
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
}

function journalOptionQuantity(row) {
  const qty = Number(row?.transferQuantity ?? row?.quantity);
  return Number.isFinite(qty) ? Math.abs(qty) : null;
}

function formatJournalOptionLabel(row) {
  const journalId = row.stockJournalId || row.id || "—";
  const productName = row.productName || row.productId || "—";
  const date = formatJournalOptionDate(row.date);
  const qty = journalOptionQuantity(row);
  return `${journalId} | ${productName} | ${date} | Qty: ${qty ?? "—"}`;
}

function usableDisplayName(...candidates) {
  for (const candidate of candidates) {
    const label = String(candidate || "").trim();
    if (label && label !== "—") return label;
  }
  return "";
}

function findLookupByIdOrName(list, id, name) {
  const sid = id != null && String(id).trim() !== "" ? String(id) : "";
  const label = usableDisplayName(name);
  const normalizedLabel = label.toLowerCase();
  if (sid) {
    const byId = (list || []).find((item) => String(item.id) === sid);
    if (byId) {
      return { id: sid, name: byId.name || label || sid };
    }
    return { id: sid, name: label || sid };
  }
  if (!normalizedLabel) return null;
  const byName = (list || []).find(
    (item) => String(item.name || "").trim().toLowerCase() === normalizedLabel
  );
  if (!byName) return null;
  return { id: String(byName.id), name: byName.name || label };
}

function mapVendorLookup(v) {
  if (!v) return null;
  const id = v.id ?? v.vendorId ?? v.vendor_id;
  if (id == null || String(id).trim() === "") return null;
  return {
    id: String(id),
    name:
      v.tradeName ||
      v.legalCompanyName ||
      v.name ||
      String(id),
  };
}

function mapWarehouseLookup(w) {
  if (!w) return null;
  const id = w.id ?? w.warehouseId ?? w.warehouse_id ?? w.warehouseLocationId;
  if (id == null || String(id).trim() === "") return null;
  return {
    id: String(id),
    name: w.name || w.warehouseName || String(id),
  };
}

async function resolveVendorLookup(list, id, name) {
  const local = findLookupByIdOrName(list, id, name);
  if (local?.id) {
    if (usableDisplayName(local.name) && local.name !== local.id) return local;
  }
  const sid = id != null && String(id).trim() !== "" ? String(id) : "";
  if (sid) {
    try {
      const full = await fetchVendorById(sid);
      const mapped = mapVendorLookup(full);
      if (mapped) return mapped;
    } catch {
      /* fall through to name search */
    }
    if (local) return local;
  }
  const label = usableDisplayName(name);
  if (!label) return local;
  try {
    const byTrade = await searchVendorsByTradeName(label, {
      page: 0,
      size: 10,
    });
    const tradeHit = mapVendorLookup(
      (byTrade?.vendors || byTrade?.content || [])[0]
    );
    if (tradeHit) return tradeHit;
  } catch {
    /* try legal name next */
  }
  try {
    const byLegal = await searchVendorsByLegalName(label, {
      page: 0,
      size: 10,
    });
    const legalHit = mapVendorLookup(
      (byLegal?.vendors || byLegal?.content || [])[0]
    );
    if (legalHit) return legalHit;
  } catch {
    /* keep local fallback */
  }
  return local;
}

async function resolveWarehouseLookup(list, id, name) {
  const local = findLookupByIdOrName(list, id, name);
  if (local?.id) {
    if (usableDisplayName(local.name) && local.name !== local.id) return local;
  }
  const sid = id != null && String(id).trim() !== "" ? String(id) : "";
  if (sid) {
    try {
      const full = await fetchWarehouseById(sid);
      const mapped = mapWarehouseLookup(full);
      if (mapped) return mapped;
    } catch {
      /* fall through to name search */
    }
    if (local) return local;
  }
  const label = usableDisplayName(name);
  if (!label) return local;
  try {
    const byName = await searchWarehousesByName(label, {
      page: 0,
      size: 10,
    });
    const hit = mapWarehouseLookup(
      (byName?.warehouses || byName?.content || [])[0]
    );
    if (hit) return hit;
  } catch {
    /* keep local fallback */
  }
  return local;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function buildImagesPayload(images) {
  if (!Array.isArray(images) || images.length === 0) return null;
  const encoded = await Promise.all(
    images.map(async (item) => {
      if (item?.file instanceof File) {
        return {
          fileName: item.file.name,
          contentType: item.file.type || "application/octet-stream",
          fileByte: await fileToBase64(item.file),
        };
      }
      if (typeof item === "string" && item.trim()) return item;
      const fileByte = item?.fileByte || item?.base64 || item?.value?.fileByte;
      const url = item?.url || item?.value?.url || null;
      if (fileByte || url) {
        return {
          fileName: item.fileName || item.name || item.value?.fileName || null,
          contentType:
            item.contentType || item.value?.contentType || null,
          fileByte: fileByte || null,
          url,
        };
      }
      return null;
    })
  );
  const cleaned = encoded.filter(Boolean);
  return cleaned.length > 0 ? cleaned : null;
}

function resolveImageSrc(item) {
  if (!item) return null;
  if (item.file instanceof File) {
    return { src: URL.createObjectURL(item.file), revoke: true };
  }
  if (item.url) return { src: item.url, revoke: false };
  const fileByte =
    item.fileByte ||
    item.base64 ||
    (typeof item.value === "string" ? item.value : item.value?.fileByte);
  if (!fileByte) return null;
  const text = String(fileByte);
  if (text.startsWith("data:") || text.startsWith("blob:") || text.startsWith("http")) {
    return { src: text, revoke: false };
  }
  const contentType =
    item.contentType || item.value?.contentType || "image/jpeg";
  return { src: `data:${contentType};base64,${text}`, revoke: false };
}

function getApiErrorMessage(err, fallback) {
  if (!err) return fallback;
  const candidates = [
    err.message,
    typeof err.data === "string" ? err.data : null,
    err.data?.message,
    Array.isArray(err.data)
      ? err.data
          .map((item) =>
            typeof item === "string"
              ? item
              : item?.message || item?.error || null
          )
          .filter(Boolean)
          .join(", ")
      : null,
  ];
  for (const candidate of candidates) {
    const text = candidate == null ? "" : String(candidate).trim();
    if (text && text !== "[object Object]") return text;
  }
  return fallback;
}

/**
 * Create-only inventory-in receipt dialog.
 * Source type is fixed via props (not shown in the UI) and always included in the payload.
 *
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   onSuccess: () => void,
 *   sourceType: string,
 *   labelKey: string,
 * }} props
 */
export function InventoryInFormModal({
  open,
  onOpenChange,
  onSuccess,
  sourceType,
  labelKey,
}) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const [products, setProducts] = useState([]);
  const [productPage, setProductPage] = useState(0);
  const [productHasMore, setProductHasMore] = useState(false);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const productFetchLock = useRef(false);

  const [warehouses, setWarehouses] = useState([]);
  const [warehousePage, setWarehousePage] = useState(0);
  const [warehouseHasMore, setWarehouseHasMore] = useState(false);
  const [loadingMoreWarehouses, setLoadingMoreWarehouses] = useState(false);
  const warehouseFetchLock = useRef(false);

  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [refJournals, setRefJournals] = useState([]);
  const [refJournalPage, setRefJournalPage] = useState(0);
  const [refJournalHasMore, setRefJournalHasMore] = useState(false);
  const [loadingRefJournals, setLoadingRefJournals] = useState(false);
  const [loadingMoreRefJournals, setLoadingMoreRefJournals] = useState(false);
  const refJournalFetchLock = useRef(false);
  const refJournalFillSeq = useRef(0);

  const [formData, setFormData] = useState(() => createEmptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const previewObjectUrlRef = useRef(null);

  const lockJournalDerivedFields = usesWarDefRefJournal(sourceType);

  const resetForm = useCallback((warehouseList = []) => {
    setFormData({
      ...createEmptyForm(),
      receiptDate: todayIsoDate(),
      returnDate: todayIsoDate(),
      invoiceDate: todayIsoDate(),
      warehouseId: warehouseList[0]?.id ? String(warehouseList[0].id) : "",
    });
    setFormErrors({});
    setSubmitError("");
    setLoadError("");
  }, []);

  const loadProducts = useCallback(async (page, { append = false } = {}) => {
    if (append && productFetchLock.current) return;
    if (!append) productFetchLock.current = false;
    if (productFetchLock.current) return;
    productFetchLock.current = true;
    if (append) setLoadingMoreProducts(true);

    try {
      const result = await inventoryManagementService.getInventoryProducts({
        page,
        size: LOOKUP_PAGE_SIZE,
      });
      setProducts((prev) =>
        append ? mergeUniqueById(prev, result.content) : result.content || []
      );
      setProductPage(result.page ?? page);
      setProductHasMore(Boolean(result.hasMore));
      return result;
    } catch (err) {
      console.error("Failed to load inventory products:", err);
      if (!append) setProducts([]);
      setProductHasMore(false);
      throw err;
    } finally {
      setLoadingMoreProducts(false);
      productFetchLock.current = false;
    }
  }, []);

  const loadWarehouses = useCallback(async (page, { append = false } = {}) => {
    if (append && warehouseFetchLock.current) return;
    if (!append) warehouseFetchLock.current = false;
    if (warehouseFetchLock.current) return;
    warehouseFetchLock.current = true;
    if (append) setLoadingMoreWarehouses(true);

    try {
      const result = await inventoryManagementService.getWarehouses({
        page,
        size: LOOKUP_PAGE_SIZE,
      });
      setWarehouses((prev) =>
        append ? mergeUniqueById(prev, result.content) : result.content || []
      );
      setWarehousePage(result.page ?? page);
      setWarehouseHasMore(Boolean(result.hasMore));
      return result;
    } catch (err) {
      console.error("Failed to load warehouses:", err);
      if (!append) setWarehouses([]);
      setWarehouseHasMore(false);
      throw err;
    } finally {
      setLoadingMoreWarehouses(false);
      warehouseFetchLock.current = false;
    }
  }, []);

  const loadRefJournals = useCallback(
    async (page, movementType, { append = false } = {}) => {
      if (!movementType) {
        setRefJournals([]);
        setRefJournalPage(0);
        setRefJournalHasMore(false);
        return null;
      }
      if (append && refJournalFetchLock.current) return;
      if (!append) refJournalFetchLock.current = false;
      if (refJournalFetchLock.current) return;
      refJournalFetchLock.current = true;
      if (append) setLoadingMoreRefJournals(true);
      else setLoadingRefJournals(true);

      try {
        const result = await inventoryManagementService.getStockJournal({
          page,
          size: JOURNAL_LOOKUP_PAGE_SIZE,
          movementType,
        });
        const content = result.content || [];
        setRefJournals((prev) =>
          append ? mergeUniqueById(prev, content) : content
        );
        const pageNumber = result.pageNumber ?? result.page ?? page;
        setRefJournalPage(pageNumber);
        const totalPages = Number(result.totalPages);
        const hasMore =
          result.last != null
            ? !Boolean(result.last)
            : Number.isFinite(totalPages) && totalPages > 0
              ? pageNumber + 1 < totalPages
              : content.length >= JOURNAL_LOOKUP_PAGE_SIZE;
        setRefJournalHasMore(Boolean(hasMore));
        return result;
      } catch (err) {
        console.error("Failed to load reference stock journals:", err);
        if (!append) setRefJournals([]);
        setRefJournalHasMore(false);
        throw err;
      } finally {
        setLoadingRefJournals(false);
        setLoadingMoreRefJournals(false);
        refJournalFetchLock.current = false;
      }
    },
    []
  );

  useEffect(() => {
    if (!open) return undefined;

    let active = true;
    setLoading(true);
    setLoadError("");
    resetForm();

    Promise.all([
      inventoryManagementService.getInventoryProducts({
        page: 0,
        size: LOOKUP_PAGE_SIZE,
      }),
      inventoryManagementService.getWarehouses({
        page: 0,
        size: LOOKUP_PAGE_SIZE,
      }),
      inventoryManagementService.getVendors(),
      inventoryManagementService.getCustomers(),
    ])
      .then(([pPage, wPage, v, c]) => {
        if (!active) return;
        const nextWarehouses = wPage?.content || [];
        setProductPage(pPage?.page ?? 0);
        setProductHasMore(Boolean(pPage?.hasMore));
        setWarehousePage(wPage?.page ?? 0);
        setWarehouseHasMore(Boolean(wPage?.hasMore));
        setProducts(pPage?.content || []);
        setWarehouses(nextWarehouses);
        setVendors(v || []);
        setCustomers(c || []);
        resetForm(nextWarehouses);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load inventory receipt form:", err);
        setLoadError(
          getApiErrorMessage(err, t("validation.failedToLoadDropdownMetadata"))
        );
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, sourceType, resetForm, t]);

  const handleProductScroll = (e) => {
    if (!productHasMore || loadingMoreProducts || loading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      loadProducts(productPage + 1, { append: true }).catch(() => {});
    }
  };

  const handleWarehouseScroll = (e) => {
    if (!warehouseHasMore || loadingMoreWarehouses || loading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      loadWarehouses(warehousePage + 1, { append: true }).catch(() => {});
    }
  };

  const applyRefJournalLookups = useCallback((lookups) => {
    if (!lookups) return;
    const { product, warehouse, vendor } = lookups;

    setFormData((prev) => {
      const next = { ...prev };
      if (product?.id) next.productId = product.id;
      if (warehouse?.id) next.warehouseId = warehouse.id;
      if (vendor?.id) next.vendorId = vendor.id;
      return next;
    });

    if (product?.id) {
      setProducts((prev) => ensureLookupOption(prev, product.id, product.name));
    }
    if (warehouse?.id) {
      setWarehouses((prev) =>
        ensureLookupOption(prev, warehouse.id, warehouse.name)
      );
    }
    if (vendor?.id) {
      setVendors((prev) => ensureLookupOption(prev, vendor.id, vendor.name));
    }

    setFormErrors((prev) => {
      if (
        !prev.productId &&
        !prev.warehouseId &&
        !prev.vendorId &&
        !prev.warrantyStartDate &&
        !prev.warrantyEndDate
      ) {
        return prev;
      }
      const next = { ...prev };
      if (product?.id) delete next.productId;
      if (warehouse?.id) delete next.warehouseId;
      if (vendor?.id) delete next.vendorId;
      if (product?.id) {
        delete next.warrantyStartDate;
        delete next.warrantyEndDate;
      }
      return next;
    });
  }, []);

  const fillFieldsFromRefJournal = useCallback(
    async (selectedJournal) => {
      if (!selectedJournal) return;
      const seq = ++refJournalFillSeq.current;

      const product = selectedJournal.productId
        ? {
            id: String(selectedJournal.productId),
            name:
              selectedJournal.productName ||
              String(selectedJournal.productId),
          }
        : null;

      const [warehouse, vendor] = await Promise.all([
        resolveWarehouseLookup(
          warehouses,
          selectedJournal.warehouseLocationId,
          usableDisplayName(
            selectedJournal.warehouseName,
            selectedJournal.fromName
          )
        ),
        resolveVendorLookup(
          vendors,
          selectedJournal.vendorId,
          usableDisplayName(selectedJournal.vendorName, selectedJournal.toName)
        ),
      ]);

      if (seq !== refJournalFillSeq.current) return;
      applyRefJournalLookups({ product, warehouse, vendor });
    },
    [warehouses, vendors, applyRefJournalLookups]
  );

  const handleChange = (key, value) => {
    setSubmitError("");

    if (
      lockJournalDerivedFields &&
      (key === "productId" || key === "warehouseId" || key === "vendorId")
    ) {
      return;
    }

    setFormData((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "defectiveQty" && !(Number(value) > 0)) {
        next.defectiveImages = [];
      }
      return next;
    });

    if (key === "warDefRefJournalId" && value) {
      const selectedJournal = (refJournals || []).find(
        (row) => String(row.stockJournalId || row.id) === String(value)
      );
      if (selectedJournal) {
        if (selectedJournal.productId) {
          applyRefJournalLookups({
            product: {
              id: String(selectedJournal.productId),
              name:
                selectedJournal.productName ||
                String(selectedJournal.productId),
            },
          });
        }
        fillFieldsFromRefJournal(selectedJournal).catch((err) => {
          console.error("Failed to fill fields from reference journal:", err);
        });
      }
    }

    if (
      formErrors[key] ||
      (key === "defectiveQty" && formErrors.defectiveImages) ||
      (key === "warDefRefJournalId" && formErrors.receivedQty) ||
      (key === "receivedQty" && formErrors.receivedQty)
    ) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        if (key === "defectiveQty") delete next.defectiveImages;
        if (key === "warDefRefJournalId") delete next.receivedQty;
        return next;
      });
    }
    if (key === "productId") {
      setFormErrors((prev) => {
        if (!prev.warrantyStartDate && !prev.warrantyEndDate) return prev;
        const next = { ...prev };
        delete next.warrantyStartDate;
        delete next.warrantyEndDate;
        return next;
      });
    }
  };

  const appendImages = (fieldKey, files) => {
    const picked = Array.isArray(files) ? files : Array.from(files || []);
    if (!picked.length) return;

    setFormData((prev) => {
      const existing = prev[fieldKey] || [];
      const remainingSlots = Math.max(0, MAX_IMAGES_PER_FIELD - existing.length);
      if (remainingSlots <= 0) {
        setFormErrors((errs) => ({
          ...errs,
          [fieldKey]: t("validation.maxImagesExceeded", {
            count: MAX_IMAGES_PER_FIELD,
          }),
        }));
        return prev;
      }

      const accepted = [];
      const oversizedNames = [];
      for (const file of picked) {
        if (accepted.length >= remainingSlots) break;
        if (file.size > MAX_IMAGE_BYTES) {
          oversizedNames.push(file.name);
          continue;
        }
        accepted.push({
          key: `${Date.now()}-${file.name}-${file.size}-${accepted.length}`,
          name: file.name,
          file,
        });
      }

      setFormErrors((errs) => {
        const next = { ...errs };
        if (oversizedNames.length > 0) {
          next[fieldKey] = t("validation.imageFileTooLarge", {
            file: oversizedNames[0],
            size: "500 KB",
          });
        } else if (picked.length > remainingSlots) {
          next[fieldKey] = t("validation.maxImagesExceeded", {
            count: MAX_IMAGES_PER_FIELD,
          });
        } else {
          delete next[fieldKey];
        }
        return next;
      });

      if (!accepted.length) return prev;
      return {
        ...prev,
        [fieldKey]: [...existing, ...accepted],
      };
    });
  };

  const handleInventoryImagesChange = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    appendImages("inventoryImages", picked);
  };

  const handleDefectiveImagesChange = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    appendImages("defectiveImages", picked);
  };

  const removeInventoryImage = (key) => {
    setFormData((prev) => ({
      ...prev,
      inventoryImages: (prev.inventoryImages || []).filter((item) => item.key !== key),
    }));
    setFormErrors((prev) => {
      if (!prev.inventoryImages) return prev;
      const next = { ...prev };
      delete next.inventoryImages;
      return next;
    });
  };

  const removeDefectiveImage = (key) => {
    setFormData((prev) => ({
      ...prev,
      defectiveImages: (prev.defectiveImages || []).filter((item) => item.key !== key),
    }));
    setFormErrors((prev) => {
      if (!prev.defectiveImages) return prev;
      const next = { ...prev };
      delete next.defectiveImages;
      return next;
    });
  };

  const closeImagePreview = useCallback(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
    setImagePreview(null);
  }, []);

  const openImagePreview = useCallback(
    (item) => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
      const resolved = resolveImageSrc(item);
      if (!resolved?.src) {
        setSubmitError(t("validation.imagePreviewUnavailable"));
        return;
      }
      if (resolved.revoke) {
        previewObjectUrlRef.current = resolved.src;
      }
      setImagePreview({
        name: item.name || t("inventory.imagePreviewTitle"),
        src: resolved.src,
      });
    },
    [t]
  );

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!open || !usesWarDefRefJournal(sourceType)) {
      setRefJournals([]);
      setRefJournalPage(0);
      setRefJournalHasMore(false);
      setLoadingRefJournals(false);
      return;
    }
    const movementType = warDefRefMovementType(sourceType);
    let active = true;
    loadRefJournals(0, movementType, { append: false }).catch(() => {
      if (!active) return;
    });
    return () => {
      active = false;
    };
  }, [open, sourceType, loadRefJournals]);

  const handleRefJournalScroll = (e) => {
    if (!refJournalHasMore || loadingMoreRefJournals || loadingRefJournals) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      const movementType = warDefRefMovementType(sourceType);
      loadRefJournals(refJournalPage + 1, movementType, { append: true }).catch(
        () => {}
      );
    }
  };

  const acceptedQty = useMemo(() => {
    const rx = Number(formData.receivedQty) || 0;
    const df = Number(formData.defectiveQty) || 0;
    return Math.max(0, rx - df);
  }, [formData.receivedQty, formData.defectiveQty]);

  const qtyError = useMemo(() => {
    const rx = Number(formData.receivedQty) || 0;
    const df = Number(formData.defectiveQty) || 0;
    if (df > rx) {
      return t("validation.defectiveExceedsReceived");
    }
    return null;
  }, [formData.receivedQty, formData.defectiveQty, t]);

  const selectedRefJournalQty = useMemo(() => {
    if (!usesWarDefRefJournal(sourceType) || !formData.warDefRefJournalId) {
      return null;
    }
    const selected = (refJournals || []).find(
      (row) =>
        String(row.stockJournalId || row.id) ===
        String(formData.warDefRefJournalId)
    );
    return journalOptionQuantity(selected);
  }, [sourceType, formData.warDefRefJournalId, refJournals]);

  const refJournalQtyError = useMemo(() => {
    if (selectedRefJournalQty == null) return null;
    const rx = Number(formData.receivedQty) || 0;
    if (rx > selectedRefJournalQty) {
      return t("validation.receivedQtyExceedsRefJournal", {
        qty: selectedRefJournalQty,
      });
    }
    return null;
  }, [selectedRefJournalQty, formData.receivedQty, t]);

  const showDefectiveDetails = Number(formData.defectiveQty) > 0;

  const isUnderWarranty = useMemo(() => {
    if (!formData.productId) return false;
    const selected = products.find(
      (p) => String(p.id) === String(formData.productId)
    );
    return Boolean(selected?.underWarranty);
  }, [products, formData.productId]);

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      if (submitting) return;
      closeImagePreview();
      onOpenChange(false);
      return;
    }
    onOpenChange(true);
  };

  const handleCancel = () => {
    if (submitting) return;
    closeImagePreview();
    onOpenChange(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!formData.productId) {
      errors.productId = t("validation.fieldRequired", { field: t("inventory.product") });
    }
    if (!formData.warehouseId) {
      errors.warehouseId = t("validation.fieldRequired", {
        field: t("inventory.warehouseLocation"),
      });
    }
    if (Number(formData.receivedQty) <= 0) {
      errors.receivedQty = t("validation.qtyMustBeGreaterThanZero", {
        field: t("inventory.receivedQuantity"),
      });
    } else if (refJournalQtyError) {
      errors.receivedQty = refJournalQtyError;
    }
    if (qtyError) {
      errors.defectiveQty = qtyError;
    }
    if (
      formData.purchasePricePerUnit === "" ||
      formData.purchasePricePerUnit === null ||
      Number(formData.purchasePricePerUnit) <= 0
    ) {
      errors.purchasePricePerUnit = t("validation.qtyMustBeGreaterThanZero", {
        field: t("inventory.purchasePricePerUnit"),
      });
    }
    if (usesWarDefRefJournal(sourceType) && !formData.warDefRefJournalId) {
      errors.warDefRefJournalId = t("validation.fieldRequired", {
        field: t("inventory.warDefRefJournal"),
      });
    }
    if (
      usesCustomerWarrantyReplacementType(sourceType) &&
      !formData.customerWarrantyReplacementType
    ) {
      errors.customerWarrantyReplacementType = t("validation.fieldRequired", {
        field: t("inventory.stockWarrantyType"),
      });
    }

    if (usesPurchaseFields(sourceType)) {
      if (!formData.batchNumber) {
        errors.batchNumber = t("validation.fieldRequired", {
          field: t("inventory.batchNumber"),
        });
      }
      if (!formData.receiptDate) {
        errors.receiptDate = t("validation.fieldRequired", {
          field: t("dateTime.receiptDate"),
        });
      }
      if (!formData.vendorId) {
        errors.vendorId = t("validation.fieldRequired", {
          field: t("inventory.vendorName"),
        });
      }
    } else {
      if (!formData.customerId) {
        errors.customerId = t("validation.fieldRequired", {
          field: t("inventory.customerName"),
        });
      }
      if (!formData.returnDate) {
        errors.returnDate = t("validation.fieldRequired", {
          field: t("dateTime.returnDate"),
        });
      }
    }

    if (isUnderWarranty) {
      if (!formData.warrantyStartDate) {
        errors.warrantyStartDate = t("validation.fieldRequired", {
          field: t("inventory.warrantyStart"),
        });
      }
      if (!formData.warrantyEndDate) {
        errors.warrantyEndDate = t("validation.fieldRequired", {
          field: t("inventory.warrantyEnd"),
        });
      }
    }

    const inventoryImageCount = (formData.inventoryImages || []).length;
    if (inventoryImageCount > MAX_IMAGES_PER_FIELD) {
      errors.inventoryImages = t("validation.maxImagesExceeded", {
        count: MAX_IMAGES_PER_FIELD,
      });
    }
    if (showDefectiveDetails) {
      const defectiveImageCount = (formData.defectiveImages || []).length;
      if (defectiveImageCount > MAX_IMAGES_PER_FIELD) {
        errors.defectiveImages = t("validation.maxImagesExceeded", {
          count: MAX_IMAGES_PER_FIELD,
        });
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const defectiveImagesPayload = showDefectiveDetails
        ? await buildImagesPayload(formData.defectiveImages)
        : null;
      const inventoryImagesPayload = await buildImagesPayload(
        formData.inventoryImages
      );
      const remarksPayload = formData.remarks?.trim() || null;

      const payload = {
        sourceType,
        productId: formData.productId,
        warehouseLocationId: formData.warehouseId,
        receivedQuantity: Number(formData.receivedQty),
        defectiveQuantity: Number(formData.defectiveQty),
        acceptedQuantity: acceptedQty,
        purchasePricePerUnit: Number(formData.purchasePricePerUnit),
        warrantyStartDate: formData.warrantyStartDate,
        warrantyEndDate: formData.warrantyEndDate,
        remarks: remarksPayload,
        inventoryImages: inventoryImagesPayload,
        defectiveImages: defectiveImagesPayload,
        ...(usesWarDefRefJournal(sourceType)
          ? { warDefRefJournalId: formData.warDefRefJournalId }
          : {}),
        ...(usesCustomerWarrantyReplacementType(sourceType)
          ? {
              customerWarrantyReplacementType:
                formData.customerWarrantyReplacementType || null,
            }
          : {}),
        ...(usesPurchaseFields(sourceType)
          ? {
              batchNumber: formData.batchNumber,
              receiptDate: formData.receiptDate,
              vendorId: formData.vendorId,
              poNumber: formData.poNumber || null,
              vendorInvoiceNumber: formData.vendorInvoiceNumber || null,
              invoiceDate: formData.invoiceDate || null,
              iofNumber: formData.iofNumber || null,
            }
          : {
              batchNumber: formData.batchNumber || null,
              returnDate: formData.returnDate,
              receiptDate: formData.returnDate,
              customerId: formData.customerId,
              customerInvoiceId: formData.customerInvoiceId,
              projectName: formData.projectName || null,
            }),
      };

      await inventoryManagementService.saveInventoryReceipt(payload);
      setSubmitting(false);
      closeImagePreview();
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to save inventory receipt:", err);
      setSubmitError(
        getApiErrorMessage(err, t("validation.inventoryRecordSaveError"))
      );
      setSubmitting(false);
    }
  };

  const isPurchaseFromVendor =
    usesPurchaseFields(sourceType) && !usesWarDefRefJournal(sourceType);
  const isVendorWarDefReplacement = usesWarDefRefJournal(sourceType);
  const isCustomerWarDefReplacement =
    usesCustomerWarrantyReplacementType(sourceType);
  const isExcessReturnFromCustomer =
    !usesPurchaseFields(sourceType) && !isCustomerWarDefReplacement;

  const invoiceNumberField = (
    <div className="space-y-2 col-span-1">
      <label className={fieldLabelClass}>{t("inventory.invoiceNumber")}</label>
      <input
        type="text"
        value={
          usesPurchaseFields(sourceType)
            ? formData.vendorInvoiceNumber
            : formData.customerInvoiceId
        }
        onChange={(e) =>
          handleChange(
            usesPurchaseFields(sourceType)
              ? "vendorInvoiceNumber"
              : "customerInvoiceId",
            e.target.value
          )
        }
        className={cn(fieldInputClass, "border-(--color-text-border)")}
      />
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogPortal>
          <DialogOverlay className="z-[60] bg-[#0a1230]/40 backdrop-blur-xs" />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-1/2 top-1/2 z-[70] w-[95vw] max-w-[1040px] max-h-[90vh] -translate-x-1/2 -translate-y-1/2",
              "flex flex-col gap-4 rounded-2xl border border-white/60 bg-white p-6 font-sans",
              "shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] focus:outline-hidden"
            )}
          >
            <div className="flex items-center justify-between shrink-0">
              <DialogTitle className="pr-4 text-[16px] font-bold text-foreground">
                {t(labelKey)}
              </DialogTitle>
              <DialogPrimitive.Close
                disabled={submitting}
                className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition focus:outline-hidden disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">{t("formActions.close")}</span>
              </DialogPrimitive.Close>
            </div>

            <div className="overflow-y-auto max-h-[70vh] pr-1">
              {loading ? (
                <div className="h-[320px] animate-pulse rounded-2xl bg-gray-50/40" />
              ) : loadError ? (
                <div className="p-8 text-center text-rose-600 font-semibold text-sm">
                  ⚠️ {loadError}
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {submitError ? (
                    <div className="p-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold">
                      {submitError}
                    </div>
                  ) : null}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
                    {isCustomerWarDefReplacement ? (
                      <div className="space-y-2 col-span-1">
                        <label className={fieldLabelClass}>
                          {t("inventory.stockWarrantyType")}{" "}
                          <span className="text-rose-500">*</span>
                        </label>
                        <Select
                          value={formData.customerWarrantyReplacementType}
                          onValueChange={(val) =>
                            handleChange("customerWarrantyReplacementType", val)
                          }
                        >
                          <SelectTrigger
                            className={cn(
                              selectTriggerClass,
                              formErrors.customerWarrantyReplacementType
                                ? "border-rose-500"
                                : "border-(--color-text-border)"
                            )}
                          >
                            <SelectValue placeholder={t("inventory.selectWarrantyType")} />
                          </SelectTrigger>
                          <SelectContent className="z-[100]">
                            {WARRANTY_TYPE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {t(option.labelKey)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formErrors.customerWarrantyReplacementType ? (
                          <p className="text-xs text-rose-500 font-medium mt-1">
                            {formErrors.customerWarrantyReplacementType}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {isCustomerWarDefReplacement ? invoiceNumberField : null}
                    {isVendorWarDefReplacement ? (
                      <div className="space-y-2 col-span-1 md:col-span-2">
                        <label className={fieldLabelClass}>
                          {t("inventory.warDefRefJournal")}{" "}
                          <span className="text-rose-500">*</span>
                        </label>
                        <Select
                          value={formData.warDefRefJournalId}
                          onValueChange={(val) => handleChange("warDefRefJournalId", val)}
                        >
                          <SelectTrigger
                            className={cn(
                              selectTriggerClass,
                              formErrors.warDefRefJournalId
                                ? "border-rose-500"
                                : "border-(--color-text-border)"
                            )}
                          >
                            <SelectValue
                              placeholder={
                                loadingRefJournals
                                  ? t("common.loading")
                                  : t("inventory.selectWarDefRefJournal")
                              }
                            />
                          </SelectTrigger>
                          <SelectContent
                            className="z-[100]"
                            viewportClassName="h-auto max-h-60 overflow-y-auto"
                            onViewportScroll={handleRefJournalScroll}
                          >
                            {refJournals.map((row) => {
                              const value = String(row.stockJournalId || row.id);
                              return (
                                <SelectItem key={value} value={value}>
                                  {formatJournalOptionLabel(row)}
                                </SelectItem>
                              );
                            })}
                            {!loadingRefJournals && refJournals.length === 0 ? (
                              <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                                {t("inventory.noRefJournalsFound")}
                              </div>
                            ) : null}
                            {loadingMoreRefJournals ? (
                              <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                                {t("common.loading")}
                              </div>
                            ) : null}
                          </SelectContent>
                        </Select>
                        {formErrors.warDefRefJournalId ? (
                          <p className="text-xs text-rose-600 font-medium">
                            {formErrors.warDefRefJournalId}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {isVendorWarDefReplacement ? invoiceNumberField : null}

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {t("inventory.product")} <span className="text-rose-500">*</span>
                      </label>
                      <Select
                        value={formData.productId}
                        onValueChange={(val) => handleChange("productId", val)}
                        disabled={lockJournalDerivedFields}
                      >
                        <SelectTrigger
                          className={cn(
                            selectTriggerClass,
                            lockJournalDerivedFields && "bg-gray-50",
                            formErrors.productId
                              ? "border-rose-500"
                              : "border-(--color-text-border)"
                          )}
                        >
                          <SelectValue placeholder={t("inventory.selectProduct")} />
                        </SelectTrigger>
                        <SelectContent
                          className="z-[100]"
                          viewportClassName="h-auto max-h-60 overflow-y-auto"
                          onViewportScroll={handleProductScroll}
                        >
                          {products.map((p) => {
                            const isDisabled =
                              usesPurchaseFields(sourceType) &&
                              p.status === "End of Production";
                            return (
                              <SelectItem
                                key={p.id}
                                value={String(p.id)}
                                disabled={isDisabled}
                              >
                                {p.name || p.id}
                                {isDisabled
                                  ? ` (${t("inventory.endOfProduction")})`
                                  : ""}
                              </SelectItem>
                            );
                          })}
                          {loadingMoreProducts ? (
                            <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                              {t("common.loading")}
                            </div>
                          ) : null}
                        </SelectContent>
                      </Select>
                      {formErrors.productId && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.productId}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {t("inventory.warehouseLocation")}{" "}
                        <span className="text-rose-500">*</span>
                      </label>
                      <Select
                        value={formData.warehouseId}
                        onValueChange={(val) => handleChange("warehouseId", val)}
                        disabled={lockJournalDerivedFields}
                      >
                        <SelectTrigger
                          className={cn(
                            selectTriggerClass,
                            lockJournalDerivedFields && "bg-gray-50",
                            formErrors.warehouseId
                              ? "border-rose-500"
                              : "border-(--color-text-border)"
                          )}
                        >
                          <SelectValue placeholder={t("inventory.selectWarehouse")} />
                        </SelectTrigger>
                        <SelectContent
                          className="z-[100]"
                          viewportClassName="h-auto max-h-60 overflow-y-auto"
                          onViewportScroll={handleWarehouseScroll}
                        >
                          {warehouses.map((w) => (
                            <SelectItem key={w.id} value={String(w.id)}>
                              {w.name || w.id}
                            </SelectItem>
                          ))}
                          {loadingMoreWarehouses ? (
                            <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                              {t("common.loading")}
                            </div>
                          ) : null}
                        </SelectContent>
                      </Select>
                      {formErrors.warehouseId && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.warehouseId}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {t("inventory.batchNumber")}{" "}
                        {usesPurchaseFields(sourceType) ? (
                          <span className="text-rose-500">*</span>
                        ) : null}
                      </label>
                      <input
                        type="text"
                        value={formData.batchNumber}
                        onChange={(e) => handleChange("batchNumber", e.target.value)}
                        className={cn(
                          fieldInputClass,
                          formErrors.batchNumber
                            ? "border-rose-500"
                            : "border-(--color-text-border)"
                        )}
                      />
                      {formErrors.batchNumber && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.batchNumber}
                        </p>
                      )}
                    </div>

                    {isPurchaseFromVendor ? invoiceNumberField : null}

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {usesPurchaseFields(sourceType)
                          ? t("dateTime.receiptDate")
                          : t("dateTime.returnDate")}{" "}
                        <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={
                          usesPurchaseFields(sourceType)
                            ? formData.receiptDate
                            : formData.returnDate
                        }
                        onChange={(e) =>
                          handleChange(
                            usesPurchaseFields(sourceType)
                              ? "receiptDate"
                              : "returnDate",
                            e.target.value
                          )
                        }
                        className={cn(
                          fieldInputClass,
                          formErrors.receiptDate || formErrors.returnDate
                            ? "border-rose-500"
                            : "border-(--color-text-border)"
                        )}
                      />
                      {formErrors.receiptDate && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.receiptDate}
                        </p>
                      )}
                      {formErrors.returnDate && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.returnDate}
                        </p>
                      )}
                    </div>

                    {isExcessReturnFromCustomer ? invoiceNumberField : null}

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {t("inventory.receivedQuantity")}{" "}
                        <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={
                          selectedRefJournalQty != null
                            ? selectedRefJournalQty
                            : undefined
                        }
                        value={formData.receivedQty}
                        onChange={(e) =>
                          handleChange("receivedQty", Number(e.target.value))
                        }
                        className={cn(
                          fieldInputClass,
                          formErrors.receivedQty || refJournalQtyError
                            ? "border-rose-500"
                            : "border-(--color-text-border)"
                        )}
                      />
                      {(formErrors.receivedQty || refJournalQtyError) && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.receivedQty || refJournalQtyError}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {t("inventory.defectiveQuantity")}{" "}
                        <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.defectiveQty}
                        onChange={(e) =>
                          handleChange("defectiveQty", Number(e.target.value))
                        }
                        className={cn(
                          fieldInputClass,
                          formErrors.defectiveQty
                            ? "border-rose-500"
                            : "border-(--color-text-border)"
                        )}
                      />
                      {formErrors.defectiveQty && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.defectiveQty}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {t("inventory.acceptedQuantity")}
                      </label>
                      <div className="w-full h-10 px-3 rounded-xl border border-gray-200 bg-gray-50 flex items-center text-sm font-semibold text-foreground font-mono">
                        {acceptedQty}
                      </div>
                    </div>

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {t("inventory.purchasePricePerUnit")}{" "}
                        <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={formData.purchasePricePerUnit}
                        onChange={(e) =>
                          handleChange("purchasePricePerUnit", e.target.value)
                        }
                        className={cn(
                          fieldInputClass,
                          formErrors.purchasePricePerUnit
                            ? "border-rose-500"
                            : "border-(--color-text-border)"
                        )}
                      />
                      {formErrors.purchasePricePerUnit && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.purchasePricePerUnit}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {usesPurchaseFields(sourceType)
                          ? t("inventory.vendorName")
                          : t("inventory.customerName")}{" "}
                        <span className="text-rose-500">*</span>
                      </label>
                      <Select
                        value={
                          usesPurchaseFields(sourceType)
                            ? formData.vendorId
                            : formData.customerId
                        }
                        onValueChange={(val) =>
                          handleChange(
                            usesPurchaseFields(sourceType)
                              ? "vendorId"
                              : "customerId",
                            val
                          )
                        }
                        disabled={lockJournalDerivedFields}
                      >
                        <SelectTrigger
                          className={cn(
                            selectTriggerClass,
                            lockJournalDerivedFields && "bg-gray-50",
                            formErrors.vendorId || formErrors.customerId
                              ? "border-rose-500"
                              : "border-(--color-text-border)"
                          )}
                        >
                          <SelectValue
                            placeholder={
                              usesPurchaseFields(sourceType)
                                ? t("inventory.selectVendor")
                                : t("inventory.selectCustomer")
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="z-[100]">
                          {usesPurchaseFields(sourceType)
                            ? vendors.map((v) => (
                                <SelectItem key={v.id} value={String(v.id)}>
                                  {v.name || v.id}
                                </SelectItem>
                              ))
                            : customers.map((c) => (
                                <SelectItem key={c.id} value={String(c.id)}>
                                  {c.name || c.id}
                                </SelectItem>
                              ))}
                        </SelectContent>
                      </Select>
                      {formErrors.vendorId && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.vendorId}
                        </p>
                      )}
                      {formErrors.customerId && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.customerId}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {t("inventory.warrantyStart")}
                        {isUnderWarranty ? (
                          <span className="text-rose-500"> *</span>
                        ) : null}
                      </label>
                      <input
                        type="date"
                        value={formData.warrantyStartDate}
                        onChange={(e) =>
                          handleChange("warrantyStartDate", e.target.value)
                        }
                        className={cn(
                          fieldInputClass,
                          formErrors.warrantyStartDate
                            ? "border-rose-500"
                            : "border-(--color-text-border)"
                        )}
                      />
                      {formErrors.warrantyStartDate && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.warrantyStartDate}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {t("inventory.warrantyEnd")}
                        {isUnderWarranty ? (
                          <span className="text-rose-500"> *</span>
                        ) : null}
                      </label>
                      <input
                        type="date"
                        value={formData.warrantyEndDate}
                        onChange={(e) =>
                          handleChange("warrantyEndDate", e.target.value)
                        }
                        className={cn(
                          fieldInputClass,
                          formErrors.warrantyEndDate
                            ? "border-rose-500"
                            : "border-(--color-text-border)"
                        )}
                      />
                      {formErrors.warrantyEndDate && (
                        <p className="text-xs text-rose-500 font-medium mt-1">
                          {formErrors.warrantyEndDate}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2 col-span-1">
                      <label className={fieldLabelClass}>
                        {usesPurchaseFields(sourceType)
                          ? t("inventory.poNumber")
                          : t("inventory.projectName")}
                      </label>
                      <input
                        type="text"
                        value={
                          usesPurchaseFields(sourceType)
                            ? formData.poNumber
                            : formData.projectName
                        }
                        onChange={(e) =>
                          handleChange(
                            usesPurchaseFields(sourceType)
                              ? "poNumber"
                              : "projectName",
                            e.target.value
                          )
                        }
                        className={cn(fieldInputClass, "border-(--color-text-border)")}
                      />
                    </div>

                    {usesPurchaseFields(sourceType) && (
                      <div className="space-y-2 col-span-1">
                        <label className={fieldLabelClass}>
                          {t("dateTime.invoiceDate")}
                        </label>
                        <input
                          type="date"
                          value={formData.invoiceDate}
                          onChange={(e) =>
                            handleChange("invoiceDate", e.target.value)
                          }
                          className={cn(
                            fieldInputClass,
                            "border-(--color-text-border)"
                          )}
                        />
                      </div>
                    )}

                    {usesPurchaseFields(sourceType) && (
                      <div className="space-y-2 col-span-1">
                        <label className={fieldLabelClass}>
                          {t("inventory.iofNumber")}
                        </label>
                        <input
                          type="text"
                          value={formData.iofNumber}
                          onChange={(e) =>
                            handleChange("iofNumber", e.target.value)
                          }
                          className={cn(
                            fieldInputClass,
                            "border-(--color-text-border)"
                          )}
                        />
                      </div>
                    )}

                    <div className="col-span-1 md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                      <div className="space-y-2 flex flex-col">
                        <label className={fieldLabelClass}>
                          {t("inventory.inventoryImages")}{" "}
                          <span className="text-xs font-medium per-file-hint normal-case">
                            {t("common.optional")}
                          </span>
                        </label>
                        <label className="relative h-14 flex flex-row items-center justify-center gap-2 px-3 rounded-xl border border-dashed border-(--color-text-border) bg-workspace/30 text-sm font-semibold text-foreground hover:bg-workspace/50 hover:border-primary/40 transition cursor-pointer">
                          <Paperclip className="h-3.5 w-3.5 per-file-hint shrink-0" />
                          <span className="truncate">
                            {t("inventory.uploadInventoryImages")}
                          </span>
                          <span className="text-[10px] font-medium per-file-hint truncate hidden sm:inline">
                            ({t("inventory.imagesUploadHint")})
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleInventoryImagesChange}
                            className="sr-only"
                          />
                        </label>
                        {formErrors.inventoryImages && (
                          <p className="text-xs text-rose-500 font-medium mt-1">
                            {formErrors.inventoryImages}
                          </p>
                        )}
                        {(formData.inventoryImages || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 pt-0.5">
                            {(formData.inventoryImages || []).map((item) => (
                              <span
                                key={item.key}
                                className="inline-flex items-center gap-1 h-8 pl-2.5 pr-1 rounded-lg bg-workspace/50 text-[11px] font-semibold text-foreground max-w-[240px]"
                              >
                                <button
                                  type="button"
                                  onClick={() => openImagePreview(item)}
                                  className="truncate text-left text-primary hover:underline cursor-pointer"
                                  title={t("inventory.viewImage")}
                                >
                                  {item.name}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeInventoryImage(item.key)}
                                  aria-label={t("formActions.remove")}
                                  className="h-5 w-5 grid place-items-center rounded per-file-hint hover:text-error transition cursor-pointer"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {showDefectiveDetails ? (
                        <div className="space-y-2 flex flex-col">
                          <label className={fieldLabelClass}>
                            {t("inventory.defectiveImages")}{" "}
                            <span className="text-xs font-medium per-file-hint normal-case">
                              {t("common.optional")}
                            </span>
                          </label>
                          <label className="relative h-14 flex flex-row items-center justify-center gap-2 px-3 rounded-xl border border-dashed border-(--color-text-border) bg-workspace/30 text-sm font-semibold text-foreground hover:bg-workspace/50 hover:border-primary/40 transition cursor-pointer">
                            <Paperclip className="h-3.5 w-3.5 per-file-hint shrink-0" />
                            <span className="truncate">
                              {t("inventory.uploadDefectiveImages")}
                            </span>
                            <span className="text-[10px] font-medium per-file-hint truncate hidden sm:inline">
                              ({t("inventory.imagesUploadHint")})
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleDefectiveImagesChange}
                              className="sr-only"
                            />
                          </label>
                          {formErrors.defectiveImages && (
                            <p className="text-xs text-rose-500 font-medium mt-1">
                              {formErrors.defectiveImages}
                            </p>
                          )}
                          {(formData.defectiveImages || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-0.5">
                              {(formData.defectiveImages || []).map((item) => (
                                <span
                                  key={item.key}
                                  className="inline-flex items-center gap-1 h-8 pl-2.5 pr-1 rounded-lg bg-workspace/50 text-[11px] font-semibold text-foreground max-w-[240px]"
                                >
                                  <button
                                    type="button"
                                    onClick={() => openImagePreview(item)}
                                    className="truncate text-left text-primary hover:underline cursor-pointer"
                                    title={t("inventory.viewImage")}
                                  >
                                    {item.name}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeDefectiveImage(item.key)}
                                    aria-label={t("formActions.remove")}
                                    className="h-5 w-5 grid place-items-center rounded per-file-hint hover:text-error transition cursor-pointer"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="hidden md:block" />
                      )}
                    </div>

                    <div className="space-y-2 flex flex-col col-span-1 md:col-span-3">
                      <label className={fieldLabelClass}>
                        {t("common.remarks")}{" "}
                        <span className="text-xs font-medium per-file-hint normal-case">
                          {t("common.optional")}
                        </span>
                      </label>
                      <textarea
                        value={formData.remarks}
                        onChange={(e) => handleChange("remarks", e.target.value)}
                        placeholder={t("inventory.inventoryRemarksPlaceholder")}
                        className={cn(
                          "w-full min-h-[72px] px-3 py-2 rounded-xl border bg-white text-sm font-medium focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10 resize-none",
                          "border-(--color-text-border)"
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-6 border-t border-(--color-text-border)/50">
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={submitting}
                      className="h-10 px-5 rounded-xl border border-(--color-text-border) per-file-hint font-semibold text-sm hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
                    >
                      {t("formActions.cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="h-10 px-6 rounded-xl bg-primary hover:bg-accent text-white font-semibold text-sm transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting
                        ? t("inventory.saving")
                        : t("formActions.saveReceipt")}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      <Dialog
        open={Boolean(imagePreview)}
        onOpenChange={(next) => {
          if (!next) closeImagePreview();
        }}
      >
        <DialogPortal>
          <DialogOverlay className="z-[80]" />
          <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-[90] w-[min(92vw,720px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border bg-white p-4 shadow-xl focus:outline-hidden">
            <div className="mb-3 flex items-center justify-between gap-3">
              <DialogTitle className="truncate text-sm font-semibold text-foreground">
                {imagePreview?.name || t("inventory.imagePreviewTitle")}
              </DialogTitle>
              <DialogPrimitive.Close
                onClick={closeImagePreview}
                className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition cursor-pointer"
                aria-label={t("formActions.close")}
              >
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>
            {imagePreview?.src ? (
              <div className="max-h-[70vh] overflow-auto rounded-xl bg-workspace/40 p-2">
                <img
                  src={imagePreview.src}
                  alt={imagePreview.name || t("inventory.imagePreviewTitle")}
                  className="mx-auto max-h-[65vh] w-auto max-w-full object-contain"
                />
              </div>
            ) : null}
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </>
  );
}
